import React, { useState, useEffect, useCallback } from 'react';
import { getClientDomain, getToken, generateResponse, translate } from '../../utils/Common';
import { BLI_PERMISSIONS_STORAGE_KEY, notifyBliPermissionsUpdated, parseBliPermissionsFromSession } from '../../config/bliPermissionsRuntime';
import { PROVIDERS_STORAGE_KEY } from '../../config/providers';
import AuthContext from './index';

async function fetchBliPermissions(token) {
    if (!token) return null;
    const res = await fetch(`${getClientDomain(2)}/users/bli-permissions`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
    });
    const data = await res.json().catch(() => null);
    return { res, data };
}

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(JSON.parse(sessionStorage.getItem('user')));
    const [loading, setLoading] = useState(false);
    const [bliPermissionsLoading, setBliPermissionsLoading] = useState(false);
    const [bliPermissionsError, setBliPermissionsError] = useState(null);

    const loadBliPermissions = useCallback(async (tokenParam, { silent = false } = {}) => {
        const token = tokenParam || sessionStorage.getItem('token') || getToken?.();
        if (!token) return { ok: false, reason: 'NO_TOKEN' };

        setBliPermissionsLoading(true);
        if (!silent) setBliPermissionsError(null);

        try {
            const out = await fetchBliPermissions(token);
            const res = out?.res;
            const data = out?.data;

            const ok = data?.code === 200 && data?.payload;
            if (ok) {
                sessionStorage.setItem(BLI_PERMISSIONS_STORAGE_KEY, JSON.stringify(data.payload));
                notifyBliPermissionsUpdated();
                setBliPermissionsError(null);
                return { ok: true };
            }

            sessionStorage.removeItem(BLI_PERMISSIONS_STORAGE_KEY);
            notifyBliPermissionsUpdated();

            const reason =
                data?.message ||
                data?.detail ||
                (res && !res.ok ? `HTTP_${res.status}` : 'BLI_PERMISSIONS_UNAVAILABLE');
            if (!silent) setBliPermissionsError(String(reason));
            return { ok: false, reason };
        } catch (e) {
            console.error('loadBliPermissions', e);
            sessionStorage.removeItem(BLI_PERMISSIONS_STORAGE_KEY);
            notifyBliPermissionsUpdated();
            if (!silent) setBliPermissionsError(e?.message ? String(e.message) : 'NETWORK_ERROR');
            return { ok: false, reason: e?.message || 'NETWORK_ERROR' };
        } finally {
            setBliPermissionsLoading(false);
        }
    }, []);

    const retryLoadBliPermissions = useCallback(async () => {
        const token = sessionStorage.getItem('token') || getToken?.();
        return loadBliPermissions(token, { silent: false });
    }, [loadBliPermissions]);

    /** GET /users/bli-permissions → sessionStorage + notify (p. ej. tras guardar el rol propio). `silent` evita banner de error en fallos puntuales. */
    const refreshBliPermissions = useCallback(async () => {
        const token = sessionStorage.getItem('token') || getToken?.();
        return loadBliPermissions(token, { silent: true });
    }, [loadBliPermissions]);

    useEffect(() => {
        const controller = new AbortController();
        try {
            const checkToken = async () => {
                setLoading(true);
                
                // if (sessionStorage.getItem('token')) {
                    const response = await fetch(`${getClientDomain(2)}/users/checkToken`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'authorization': `Bearer ${sessionStorage.getItem('token')}`
                        },
                        signal: controller.signal
                    });

                    const result = await response.json();
                    if (result) {
                        if (result.user) {
                            // Mantener información previa del usuario (incluyendo roleId)
                            let storedUser = null;
                            try {
                                const stored = sessionStorage.getItem('user');
                                if (stored) {
                                    storedUser = JSON.parse(stored);
                                }
                            } catch (err) {
                                console.error('Error parsing user from sessionStorage in AuthProvider:', err);
                            }

                            const mergedUser = { ...(storedUser || {}), ...result.user };
                            setUser(mergedUser);
                            sessionStorage.setItem('user', JSON.stringify(mergedUser));
                            // BLI permissions: fetch solo en login (signIn). En recarga usar caché en sessionStorage.
                            if (!parseBliPermissionsFromSession()) {
                                await loadBliPermissions(sessionStorage.getItem('token'), { silent: false });
                            } else {
                                notifyBliPermissionsUpdated();
                            }
                        }
                        else signOut();
                        if (result.code === 401) signOut();
                    }
                    
                    setLoading(false);
                // }
            }

            if (user) checkToken();
        } catch (err) {
            console.error('AuthProvider.checkToken:', err);
        }
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signIn = async (data) => {
        try {
            const response = await fetch(`${getClientDomain(2)}/users/signin`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authentication': 'Basic ' + Buffer.from(data.username.toUpperCase() + ":" + data.pwd).toString('base64'),
                }
            });
            
            const result = await response.json();

            if (result.code !== 500 && result.message) {
                switch (result.message) {
                    case 'USER_NOT_FOUND':
                        return 'USER_NOT_FOUND';
                    case 'EMAIL_NOT_FOUND':
                        return 'EMAIL_NOT_FOUND';
                    case 'PASSWORD_INVALID':
                        return 'PASSWORD_INVALID';
                    case 'SIGNIN_SUCCESS':
                        if (result.payload?.IdEstatus === 1) {
                            setUser(result.payload);
                            setTimeout(() => {
                                signOut()
                            }, (24 * 60 * 60 * 1000))
                            sessionStorage.setItem('token', result.token);
                            sessionStorage.setItem('user', JSON.stringify(result.payload));
                            sessionStorage.setItem('currentNavPage', translate("sidebar.options.start"));
                            await loadBliPermissions(result.token, { silent: false });
                            setLoading(false);
                            return 'LOGIN_SUCCESS';
                        }
                        else if (result.payload.IdEstatus === 2) return 'USER_INACTIVATED'
                        else return 'USER_NOT_VERIFIED';
                    default:
                        break;
                }
            }
            else return 'SERVER_ERROR';
        } catch (error) {
            console.error('AuthProvider.signIn:', error);
            setLoading(false);
            return 'SERVER_ERROR';
        }
    }

    const signOut = () => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('currentNavPage');
        sessionStorage.removeItem('platforms');
        sessionStorage.removeItem('priority-incidents');
        sessionStorage.removeItem('types-incidents');
        sessionStorage.removeItem('status-incidents');
        sessionStorage.removeItem('status-packages');
        sessionStorage.removeItem(BLI_PERMISSIONS_STORAGE_KEY);
        sessionStorage.removeItem(PROVIDERS_STORAGE_KEY);
        sessionStorage.removeItem('currentPage');
        sessionStorage.removeItem('valueSearchFilter');
        setUser(null);
        // Redirigir explícitamente al login cuando el token expira
        window.location.href = '/login';
    }

    const signUp = async (data) => {
        try {
            const { username, pwd, ...fields } = data;
            const response = await fetch(`${getClientDomain(2)}/users/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authentication': 'Basic ' + Buffer.from(username.toString().trim().toUpperCase() + ":" + pwd.toString().trim()).toString('base64')
                },
                body: JSON.stringify(fields)
            });
            
            const result = await response.json();
            if (result.code === 200) {
                if (result.payload.length > 0) {
                    if (result.payload[0].Mensaje === 'USER_CREATED_SUCCESSFULLY') {
                        setLoading(false);
                        return 'SIGNUP_SUCCESSFULLY';
                    }
                    else if (result.payload[0].Mensaje === 'USER_OR_EMAIL_ALREADY_EXIST') {
                        if (result.payload[0].UsuarioValidator) return 'USERNAME_ALREADY_EXIST';
                        if (result.payload[0].EmailValidator) return 'EMAIL_ALREADY_EXIST';
                    }
                }
                else return 'SERVER_ERROR';
            }
            else return 'SERVER_ERROR';
        } catch (error) {
            setLoading(false);
            return 'SERVER_ERROR';
        }
    }

    const forgotPwd = async (email) => {
        try {
            const response = await fetch(`${getClientDomain(2)}/users/forgotPwd/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(email)
            });
            
            const result = await response.json();
            if (result.code === 200) return 'EMAIL_SENT_SUCCESSFULLY';
            else return result.message;
        } catch (error) {
            setLoading(false);
            return 'SERVER_ERROR';
        }
    }

    const resetPwd = async (data, token) => {
        try {
            const response = await fetch(`${getClientDomain(2)}/users/resetpwd`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'Credential': Buffer.from(data.pwd.toString().trim()).toString('base64')
                }
            });
            
            const result = await response.json();
            if (result.code === 200 && result.payload) return result.payload.Mensaje;
            else if (result.code === 401) return 'TOKEN_EXPIRED';
            else return 'SERVER_ERROR';
        } catch (error) {
            setLoading(false);
            return 'SERVER_ERROR';
        }
    }

    const updateInfo = async (data) => {
        try {
            const response = await fetch(`${getClientDomain(2)}/users/update/info/${data.idCliente}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify(data)
            });
            
            const result = await response.json();
            if (result.code === 200) {
                if (result.payload) return result.payload.Mensaje;
                else return 'USER_NOT_FOUND';
            }
            else if (result.code === 401) return 'TOKEN_EXPIRED';
            else return 'SERVER_ERROR';
        } catch (error) {
            setLoading(false);
            return 'SERVER_ERROR';
        }
    }

    const updatePwd = async (data) => {
        try {
            const response = await fetch(`${getClientDomain(2)}/users/update/pwd/${data.idCliente}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`,
                    'Credentials': Buffer.from(data.newPwd.toString() + ':' + data.oldPwd.toString()).toString('base64')
                }
            });
            
            const result = await response.json();
            if (result.code === 200) {
                if (result.payload) return result.payload.Mensaje;
                else return 'USER_NOT_FOUND';
            }
            else if (result.code === 401) return 'TOKEN_EXPIRED';
            else return 'SERVER_ERROR';
        } catch (error) {
            setLoading(false);
            return 'SERVER_ERROR';
        }
    }

    const updatePwdLocker = async (data) => {
        try {
            const response = await fetch(`${getClientDomain(2)}/users/update/pwdLocker/${data.idCliente}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`,
                    'Credentials': Buffer.from(data.newPwd.toString()).toString('base64')
                }
            });
            
            const result = await response.json();
            if (result.code === 200) {
                if (result.payload) return result.payload.Mensaje;
                else return 'USER_NOT_FOUND';
            }
            else if (result.code === 401) return 'TOKEN_EXPIRED';
            else return 'SERVER_ERROR';
        } catch (error) {
            setLoading(false);
            return 'SERVER_ERROR';
        }
    }

    const activateAccount = async (country, token) => {
        try {
            const response = await fetch(`${getClientDomain(2)}/users/activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${ token }`
                }
            });
            
            const result = await response.json();
            const responseObj = generateResponse(result);
            return responseObj;

        } catch (error) {
            setLoading(false);
            return translate("login.forgotpwd.validate.errorServer");
        }
    }
    
    const updateInfoBank = async (data) => {
        const response = await fetch(`${getClientDomain(2)}/users/update/infobank`, {
            method: "PUT",
            headers: {
            'Content-Type': 'application/json',
              authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify(data),
          });

          if (!response.ok) {
            console.error("Error en la solicitud:", response.status, response.statusText);
            // Puedes agregar más detalles del error si están disponibles en la respuesta.
          }

          const result = await response.json();
          if (result?.payload) return result;
          else return result;
        };

    const bankInfo = async () => {
        const response = await fetch(`${getClientDomain(2)}/users/infobank`, {
            method: "GET",
            headers: {
            'Content-Type': 'application/json',
              authorization: `Bearer ${getToken()}`,
            },
          });

          if (!response.ok) {
            console.error("Error en la solicitud:", response.status, response.statusText);
            // Puedes agregar más detalles del error si están disponibles en la respuesta.
          }

          const result = await response.json();
          if (result?.payload) return result;
          else return result;
        };

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                signIn,
                signOut,
                signUp,
                forgotPwd,
                resetPwd,
                updatePwd,
                updatePwdLocker,
                updateInfo,
                updateInfoBank,
                loading,
                setLoading,
                activateAccount,
                bankInfo,
                // BLI permissions status / retry
                bliPermissionsLoading,
                bliPermissionsError,
                retryLoadBliPermissions,
                refreshBliPermissions,
            }}
        >
            { children }
        </AuthContext.Provider>
    );
}

export default AuthProvider;